import React, { useState, ChangeEvent, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  ArrowLeft,
  AlertCircle,
  Check,
  X,
  Loader,
  ChevronDown
} from 'lucide-react';
import { CommonUtils } from '../../utils/commonUtils';
import {
  createProject,
  existsProjectName,
  getProjectList,
  type ProjectTypeCode,
} from '../../utils/project';

// Types
interface FormData {
  name: string;
  description: string;
  projectType: ProjectTypeCode;
  parentProjectCode: string;
}

interface ValidationErrors {
  name?: string;
  description?: string;
  projectType?: string;
  parentProjectCode?: string;
}

const PROJECT_TYPE_OPTIONS = [
  { value: 'STANDALONE', label: '단독' },
  { value: 'PLATFORM', label: '플랫폼' },
  { value: 'BRAND', label: '브랜드' },
] as const;

const ProjectCreate: React.FC = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    description: '',
    projectType: 'STANDALONE',
    parentProjectCode: ''
  });

  const [isNameChecking, setIsNameChecking] = useState<boolean>(false);
  const [isNameVerified, setIsNameVerified] = useState<boolean>(false);
  const [nameCheckMessage, setNameCheckMessage] = useState<string>('');
  const [lastCheckedName, setLastCheckedName] = useState<string>('');
  const [errors, setErrors] = useState<ValidationErrors>({});
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [isAdvancedOpen, setIsAdvancedOpen] = useState<boolean>(false);
  const [platformProjects, setPlatformProjects] = useState<Array<{ code: string; name: string }>>([]);

  useEffect(() => {
    const fetchPlatformProjects = async () => {
      try {
        const response = await getProjectList({
          keyword: '',
          status: '',
          type: 'PLATFORM'
        });

        const platforms = (response.projects ?? []).map((project) => ({
          code: project.code,
          name: project.name
        }));

        setPlatformProjects(platforms);

        setFormData((prev) => {
          const hasSelectedParent = platforms.some((platform) => platform.code === prev.parentProjectCode);

          if (prev.projectType === 'BRAND' && platforms.length === 0) {
            return { ...prev, projectType: 'STANDALONE', parentProjectCode: '' };
          }

          if (prev.parentProjectCode && !hasSelectedParent) {
            return { ...prev, parentProjectCode: '' };
          }

          return prev;
        });
      } catch (error) {
        console.error('플랫폼 프로젝트 목록 조회 오류:', error);
        setPlatformProjects([]);
      }
    };

    fetchPlatformProjects();
  }, []);

  // Handle name input change
  const handleNameChange = (e: ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    
    // Limit to 20 characters
    if (value.length > 20) return;
    
    setFormData(prev => ({ ...prev, name: value }));
    
    // Reset verification if name changed after verification
    if (isNameVerified && value !== lastCheckedName) {
      setIsNameVerified(false);
      setNameCheckMessage('');
    }
    
    // Clear error
    if (errors.name) {
      setErrors(prev => ({ ...prev, name: undefined }));
    }
  };

  // Handle description input change
  const handleDescriptionChange = (e: ChangeEvent<HTMLTextAreaElement>) => {
    const value = e.target.value;
    
    // Limit to 100 characters
    if (value.length > 100) return;
    
    setFormData(prev => ({ ...prev, description: value }));
    
    // Clear error
    if (errors.description) {
      setErrors(prev => ({ ...prev, description: undefined }));
    }
  };

  // Check name duplication
  const checkNameDuplication = async () => {
    if (!formData.name.trim()) {
      setErrors(prev => ({ ...prev, name: '프로젝트 이름을 입력해주세요' }));
      return;
    }

    setIsNameChecking(true);
    setNameCheckMessage('');

    try {
      const isDuplicate = await existsProjectName(formData.name);

      if (isDuplicate) {
        setIsNameVerified(false);
        setNameCheckMessage('이미 사용 중인 프로젝트 이름입니다');
        setErrors(prev => ({ ...prev, name: '다른 이름을 사용해주세요' }));
      } else {
        setIsNameVerified(true);
        setLastCheckedName(formData.name);
        setNameCheckMessage('사용 가능한 프로젝트 이름입니다');
        setErrors(prev => ({ ...prev, name: undefined }));
      }
    } catch (error) {
      setIsNameVerified(false);
      const serverMessage = error instanceof Error ? error.message : '';
      const defaultMessage = '중복 확인 중 오류가 발생했습니다';
      const fullMessage = serverMessage ? `${defaultMessage}\n\n서버 메시지: ${serverMessage}` : defaultMessage;
      setNameCheckMessage(fullMessage);
      setErrors(prev => ({ ...prev, name: '다시 시도해주세요' }));
    } finally {
      setIsNameChecking(false);
    }
  };

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: ValidationErrors = {};

    if (!formData.name.trim()) {
      newErrors.name = '프로젝트 이름을 입력해주세요';
    } else if (!isNameVerified) {
      newErrors.name = '프로젝트 이름 중복 확인이 필요합니다';
    }

    if (formData.projectType === 'BRAND' && !formData.parentProjectCode) {
      newErrors.parentProjectCode = '브랜드 프로젝트는 상위 플랫폼 선택이 필요합니다';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle form submission
  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      await createProject({
        name: formData.name,
        description: formData.description,
        type: formData.projectType,
        parentCode:
          formData.projectType === 'BRAND' ? formData.parentProjectCode.trim() : undefined,
      });

      alert('프로젝트가 성공적으로 생성되었습니다!');
      navigate('/projects');
    } catch (error: unknown) {
      console.error('프로젝트 생성 오류:', error);
      const displayMessage =
        error instanceof Error
          ? error.message
          : CommonUtils.getNetworkErrorMessage(error) || '프로젝트 생성 중 오류가 발생했습니다.';
      alert(displayMessage);
    } finally {
      setIsSubmitting(false);
    }
  };

  const hasFormInput = (): boolean => {
    if (formData.name.trim()) return true;
    if (formData.description.trim()) return true;
    if (formData.projectType !== 'STANDALONE') return true;
    if (formData.parentProjectCode.trim()) return true;
    return false;
  };

  const handleCancel = () => {
    if (!hasFormInput() || window.confirm('작성 중인 내용이 저장되지 않습니다. 취소하시겠습니까?')) {
      navigate(-1);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="h-16 bg-white border-b border-gray-200">
        <div className="max-w-4xl mx-auto h-full px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-full">
            <div className="flex items-center">
              <button
                onClick={handleCancel}
                className="mr-4 p-2 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <h1 className="text-xl font-semibold text-gray-900">새 프로젝트 만들기</h1>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 sm:p-8">
            {/* Instructions */}
            <div className="mb-8">
              <h2 className="text-lg font-semibold text-gray-900 mb-2">프로젝트 정보</h2>
              <p className="text-sm text-gray-600">
                프로젝트의 기본 정보를 입력해주세요. 프로젝트 이름은 필수 입력 사항입니다.
              </p>
            </div>

            <div className="space-y-6">
              {/* Project Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 이름 <span className="text-red-500">*</span>
                </label>
                <div className="flex gap-3">
                  <div className="flex-1">
                    <input
                      type="text"
                      value={formData.name}
                      onChange={handleNameChange}
                      placeholder="프로젝트 이름 입력 (최대 20자)"
                      className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                        errors.name ? 'border-red-500' : 'border-gray-300'
                      }`}
                      maxLength={20}
                    />
                    <div className="mt-1 flex items-center justify-between">
                      <div>
                        {errors.name && (
                          <p className="text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.name}
                          </p>
                        )}
                        {nameCheckMessage && (
                          <p className={`text-sm flex items-center ${
                            isNameVerified ? 'text-green-600' : 'text-red-500'
                          }`}>
                            {isNameVerified ? (
                              <Check className="w-4 h-4 mr-1" />
                            ) : (
                              <X className="w-4 h-4 mr-1" />
                            )}
                            {nameCheckMessage}
                          </p>
                        )}
                      </div>
                      <span className="text-xs text-gray-500">
                        {formData.name.length}/20
                      </span>
                    </div>
                  </div>
                  <button
                    onClick={checkNameDuplication}
                    disabled={isNameChecking || isNameVerified || !formData.name.trim()}
                    className={`px-4 py-2 rounded-lg transition-colors flex items-center ${
                      isNameVerified
                        ? 'bg-green-100 text-green-700 cursor-not-allowed'
                        : isNameChecking || !formData.name.trim()
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-gray-900 text-white hover:bg-gray-800'
                    }`}
                  >
                    {isNameChecking ? (
                      <>
                        <Loader className="w-4 h-4 mr-2 animate-spin" />
                        확인 중...
                      </>
                    ) : isNameVerified ? (
                      <>
                        <Check className="w-4 h-4 mr-2" />
                        확인 완료
                      </>
                    ) : (
                      '중복 확인'
                    )}
                  </button>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  프로젝트 설명
                </label>
                <textarea
                  value={formData.description}
                  onChange={handleDescriptionChange}
                  placeholder="프로젝트에 대한 간단한 설명을 입력하세요 (최대 100자)"
                  rows={3}
                  className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 resize-none ${
                    errors.description ? 'border-red-500' : 'border-gray-300'
                  }`}
                  maxLength={100}
                />
                <div className="mt-1 flex items-center justify-between">
                  {errors.description && (
                    <p className="text-sm text-red-500 flex items-center">
                      <AlertCircle className="w-4 h-4 mr-1" />
                      {errors.description}
                    </p>
                  )}
                  <span className="text-xs text-gray-500 ml-auto">
                    {formData.description.length}/100
                  </span>
                </div>
              </div>

              {/* Advanced Settings */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <button
                  type="button"
                  onClick={() => setIsAdvancedOpen((prev) => !prev)}
                  className="w-full px-4 py-3 bg-gray-50 hover:bg-gray-100 transition-colors flex items-center justify-between text-left"
                >
                  <div>
                    <p className="text-sm font-medium text-gray-900">고급 설정 (선택)</p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      프로젝트 타입과 상위 플랫폼을 지정할 수 있습니다.
                    </p>
                  </div>
                  <ChevronDown
                    className={`w-4 h-4 text-gray-500 transition-transform ${isAdvancedOpen ? 'rotate-180' : ''}`}
                  />
                </button>

                {isAdvancedOpen && (
                  <div className="p-4 space-y-4 border-t border-gray-200">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        프로젝트 타입 <span className="text-red-500">*</span>
                      </label>
                      <div className="flex flex-wrap gap-2">
                        {PROJECT_TYPE_OPTIONS.map((option) => {
                          const isBrandWithoutPlatforms = option.value === 'BRAND' && platformProjects.length === 0;
                          const isSelected = formData.projectType === option.value;
                          return (
                            <button
                              key={option.value}
                              type="button"
                              onClick={() => {
                                if (isBrandWithoutPlatforms) return;
                                setFormData((prev) => ({
                                  ...prev,
                                  projectType: option.value,
                                  parentProjectCode: option.value === 'BRAND' ? prev.parentProjectCode : ''
                                }));
                                setErrors((prev) => ({
                                  ...prev,
                                  projectType: undefined,
                                  parentProjectCode: option.value === 'BRAND' ? prev.parentProjectCode : undefined
                                }));
                              }}
                              disabled={isBrandWithoutPlatforms}
                              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${
                                isSelected
                                  ? 'bg-gray-900 text-white border-gray-900'
                                  : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
                              } ${isBrandWithoutPlatforms ? 'opacity-50 cursor-not-allowed' : ''}`}
                            >
                              {option.label}
                            </button>
                          );
                        })}
                      </div>
                      {platformProjects.length === 0 && (
                        <p className="mt-2 text-xs text-gray-500">
                          선택 가능한 플랫폼 프로젝트가 없어 현재는 단독/플랫폼 타입만 선택할 수 있습니다.
                        </p>
                      )}
                    </div>

                    {formData.projectType === 'BRAND' && (
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          상위 플랫폼 <span className="text-red-500">*</span>
                        </label>
                        <select
                          value={formData.parentProjectCode}
                          onChange={(e) => {
                            const value = e.target.value;
                            setFormData((prev) => ({ ...prev, parentProjectCode: value }));
                            if (errors.parentProjectCode && value) {
                              setErrors((prev) => ({ ...prev, parentProjectCode: undefined }));
                            }
                          }}
                          className={`w-full px-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-gray-900 ${
                            errors.parentProjectCode ? 'border-red-500' : 'border-gray-300'
                          }`}
                        >
                          <option value="">상위 플랫폼을 선택하세요</option>
                          {platformProjects.map((project) => (
                            <option key={project.code} value={project.code}>
                              {project.name} ({project.code})
                            </option>
                          ))}
                        </select>
                        {errors.parentProjectCode && (
                          <p className="mt-2 text-sm text-red-500 flex items-center">
                            <AlertCircle className="w-4 h-4 mr-1" />
                            {errors.parentProjectCode}
                          </p>
                        )}
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Form Preview */}
              <div className="mt-8 p-4 bg-gray-50 rounded-lg">
                <h3 className="text-sm font-medium text-gray-700 mb-3">미리보기</h3>
                <div className="flex items-start space-x-4">
                  <div className="w-16 h-16 bg-gray-200 rounded-lg flex items-center justify-center">
                    <div className="text-4xl">📁</div>
                  </div>
                  <div className="flex-1">
                    <h4 className="font-semibold text-gray-900">
                      {formData.name || '프로젝트 이름'}
                    </h4>
                    <p className="text-sm text-gray-600 mt-1">
                      {formData.description || '프로젝트 설명이 여기에 표시됩니다'}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="mt-8 flex justify-end space-x-3">
              <button
                onClick={handleCancel}
                className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors"
              >
                취소
              </button>
              <button
                onClick={handleSubmit}
                disabled={isSubmitting}
                className={`px-6 py-2 bg-gray-900 text-white rounded-lg transition-colors flex items-center ${
                  isSubmitting ? 'opacity-50 cursor-not-allowed' : 'hover:bg-gray-800'
                }`}
              >
                {isSubmitting ? (
                  <>
                    <Loader className="w-4 h-4 mr-2 animate-spin" />
                    생성 중...
                  </>
                ) : (
                  '프로젝트 생성'
                )}
              </button>
            </div>
          </div>
        </div>

        {/* Help Text */}
        <div className="mt-6 p-4 bg-blue-50 rounded-lg">
          <p className="text-sm text-blue-800">
            <strong>도움말:</strong> 프로젝트 생성 후에도 설정에서 정보를 수정할 수 있습니다.
          </p>
        </div>
      </div>
    </div>
  );
};

export default ProjectCreate;